import {useMemo} from 'react';

import {getTimeZones} from '@vvo/tzdb';
import {keyBy} from 'lodash';
import {useTranslation, UseTranslationResponse} from 'react-i18next';

import {buildTzArr, findTzArrKey, getCanonicalTz} from 'Utils';

const timeZonesWithUtc = getTimeZones({includeUtc: true});

export const getResolvedTimeZone = (
  t: UseTranslationResponse<'translation', undefined>['t'],
  initialTimeZone=getCanonicalTz(new Intl.DateTimeFormat().resolvedOptions().timeZone) || 'Etc/UTC',
) => {
  const timeZoneOptions = buildTzArr(timeZonesWithUtc, t);
  const indexedOpts = keyBy<{name: string, label: string}>(timeZoneOptions, (o) => o.name);
  const consolidatedTimeZone = findTzArrKey(timeZoneOptions, initialTimeZone);
  const timeZone = indexedOpts[consolidatedTimeZone]?.name ?? consolidatedTimeZone;
  return {
    timeZoneOptions,
    indexedOpts,
    timeZone,
    timeZoneLabel: indexedOpts[consolidatedTimeZone]?.label ?? consolidatedTimeZone,
  };
};

export const useResolvedTimeZone = (
  initialTimeZone=getCanonicalTz(new Intl.DateTimeFormat().resolvedOptions().timeZone) || 'Etc/UTC',
) => {
  const {t} = useTranslation();
  const timeZoneOptions = useMemo(() => buildTzArr(timeZonesWithUtc, t), [t]);
  const indexedOpts = useMemo(() => {
    return keyBy(timeZoneOptions, (o) => o.name);
  }, [timeZoneOptions]);

  const consolidatedTimeZone = useMemo(() => {
    return findTzArrKey(timeZoneOptions, initialTimeZone);
  }, [initialTimeZone, timeZoneOptions]);

  return {
    timeZoneOptions,
    indexedOpts,
    timeZone: consolidatedTimeZone,
    timeZoneLabel: indexedOpts[consolidatedTimeZone]?.label ?? consolidatedTimeZone,
  };
};
